import { CollapsibleSection } from '@athena/ui'
import type { ReactNode } from 'react'

import { useFormat } from '../../lib/format.js'
import { groupPracticesBySubject, type Subjected } from '../../lib/group-practices-by-subject.js'

export interface SubjectGroupedPracticesProps<T extends Subjected & { id: string }> {
  practices: readonly T[]
  renderPractice: (practice: T) => ReactNode
  /**
   * Makes each subject a heading of this level, where the groups are the
   * page's main sections (the tutor's practice list, directly under its h1).
   * Left out inside a section that already has its own heading, so a card's
   * h3 sits under that h2 rather than beside a heading of the same level.
   */
  headingLevel?: 2 | 3
  /**
   * The list is narrowed to a search, so every subject shown holds a match.
   * Those subjects open by themselves and count matches rather than
   * practices: a search the tutor then has to unfold subject by subject has
   * found the practice and hidden it again.
   */
  searching?: boolean
}

/**
 * A shelf of practices, split into one collapsible section per subject.
 *
 * The tutor's practice list, where every subject starts folded: a tutor with
 * forty practices is scanning for one. The student's home groups by the same
 * `groupPracticesBySubject` and folds with the same `CollapsibleSection`, but
 * opens a subject that has something to start and orders by what can be done
 * (`features/learn/StudentSubjectGroups`) — a different question, answered by
 * the same parts. A subject with nothing left in it after filtering never gets
 * a section: `groupPracticesBySubject` only produces a group for a subject that
 * actually has a practice in it.
 */
export function SubjectGroupedPractices<T extends Subjected & { id: string }>({
  practices,
  renderPractice,
  headingLevel,
  searching = false,
}: SubjectGroupedPracticesProps<T>) {
  const format = useFormat()
  const groups = groupPracticesBySubject(practices)

  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <CollapsibleSection
          // Keyed on whether a search is on, and nothing more: starting or
          // clearing a search sets every subject's fold afresh — open for a
          // search, folded for browsing — and while a search goes on, a
          // subject the tutor folded stays folded as they refine the query.
          // A subject that only now matches mounts fresh, and so opens.
          key={`${group.subject.id}:${searching ? 'search' : 'browse'}`}
          title={group.subject.name}
          meta={format.count(searching ? 'matches' : 'practices', group.practices.length)}
          defaultOpen={searching}
          {...(headingLevel ? { headingLevel } : {})}
        >
          <ul className="divide-y divide-border">
            {group.practices.map((practice) => (
              <li key={practice.id}>{renderPractice(practice)}</li>
            ))}
          </ul>
        </CollapsibleSection>
      ))}
    </div>
  )
}
